/* Two elements, one section: <vsr-clip> drives the clip and it
   See docs/js-notes/vsr-clip.md#note-1. */

(function () {
  'use strict';

  /** Enough of the frame on screen to be worth playing, and to read as "in view". */
  const PLAY_RATIO = 0.4;

  class VsrClip extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-clip-toggle]');
      if (!this.video || !this.toggle) return;

      this.controller = new AbortController();
      const { signal } = this.controller;

      // THE ONE CHECK THAT COMES BEFORE THE HANDOVER. Everything below trades
      // the platform's controls for ours, and ours only knows when to play
      // because an observer tells it. A browser without `IntersectionObserver`
      // that got past this line would be left with a video that has no
      // controls, no autoplay and no way to start — strictly worse than the
      // markup it replaced. So it keeps the markup.
      if (typeof IntersectionObserver === 'undefined') return;

      this.reduced = Boolean(window.VSR && window.VSR.prefersReducedMotion());
      // The merchant can turn automatic playback off for the whole section;
      // the toggle still works, because a clip nobody can start is not a clip.
      this.auto = this.dataset.autoplay !== 'false';
      // Null until the visitor decides; 'play' or 'pause' after that, forever.
      this.intent = null;

      // The handover: the platform's controls go, ours arrives. Both in the
      // same task, so there is no frame with neither.
      this.video.controls = false;
      this.toggle.hidden = false;
      this.toggle.addEventListener('click', () => this.onToggle(), { signal });

      // `play` and `pause` can also come from the OS, a keyboard media key or
      // the Theme Editor, so the button is painted from the video's real state
      // rather than from what we last asked for.
      this.video.addEventListener('play', () => this.paint(), { signal });
      this.video.addEventListener('pause', () => this.paint(), { signal });

      this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
        threshold: [0, PLAY_RATIO]
      });
      this.observer.observe(this.video);

      this.paint();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.controller?.abort();
      this.controller = null;

      // Handed back exactly as it was found. An element out of the document
      // with our toggle showing would be a play button wired to nothing.
      if (this.video) {
        this.video.pause();
        this.video.controls = true;
      }
      if (this.toggle) this.toggle.hidden = true;
    }

    onIntersect(entries) {
      const entry = entries[entries.length - 1];

      // First sight starts the download, not the video. The file is fetched
      // `preload="none"` so a visitor who never scrolls this far pays nothing
      // for it; the poster covers the gap between here and the first frame.
      if (entry.isIntersecting && this.video.preload !== 'auto') this.video.preload = 'auto';

      if (entry.intersectionRatio >= PLAY_RATIO) this.resume();
      else this.video.pause();
    }

    resume() {
      if (!this.auto || this.reduced || this.intent === 'pause') return;
      this.play();
    }

    /* `play()` returns a promise and browsers reject it — autoplay
   See docs/js-notes/vsr-clip.md#note-2. */
    play() {
      const started = this.video.play();
      if (started && typeof started.catch === 'function') started.catch(() => {});
    }

    onToggle() {
      if (this.video.paused) {
        this.intent = 'play';
        this.play();
      } else {
        this.intent = 'pause';
        this.video.pause();
      }
    }

    /* One button, two meanings, and the label has to say which. `a
   See docs/js-notes/vsr-clip.md#note-3. */
    paint() {
      const playing = !this.video.paused;
      const label = playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay;
      this.toggle.setAttribute('aria-label', label || '');
      this.toggle.dataset.playing = playing ? 'true' : 'false';
    }
  }

  /* <vsr-beats> — the three written moments under the clip, one
   See docs/js-notes/vsr-clip.md#note-4. */
  class VsrBeats extends HTMLElement {
    connectedCallback() {
      this.beats = Array.from(this.querySelectorAll('[data-beat]'));
      if (this.beats.length < 2) return;

      this.controller = new AbortController();
      this.beats.forEach((beat, i) => this.set(beat, i === 0));
      this.addEventListener('click', (event) => this.onClick(event), {
        signal: this.controller.signal
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      // Handed back the way it was found: every statement readable.
      this.beats?.forEach((beat) => this.set(beat, true));
    }

    onClick(event) {
      const toggle = event.target.closest('[data-beat-toggle]');
      if (!toggle || !this.contains(toggle)) return;

      const beat = toggle.closest('[data-beat]');
      const open = beat.dataset.open !== 'true';
      // Toggling the open one shut is allowed: a visitor who has read it should
      // be able to put it away without picking another.
      this.beats.forEach((other) => this.set(other, other === beat && open));
    }

    set(beat, open) {
      beat.dataset.open = open ? 'true' : 'false';
      beat
        .querySelector('[data-beat-toggle]')
        ?.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  if (window.VSR) {
    window.VSR.define('vsr-clip', VsrClip);
    window.VSR.define('vsr-beats', VsrBeats);
  } else {
    if (!customElements.get('vsr-clip')) customElements.define('vsr-clip', VsrClip);
    if (!customElements.get('vsr-beats')) customElements.define('vsr-beats', VsrBeats);
  }
})();
